/**
 * The ASS writer.
 *
 * Both the line boxes and the individual words are positioned by arithmetic
 * rather than left to a renderer's text flow: a space in a monospace face is
 * one full character advance -- too loose at caption size -- and ASS \fsp
 * cannot tighten it without loosening the inside of every word.
 *
 * The page paints from the same placements, so the ASS document is both an
 * export and the proof that the layout still equals the one libass rendered.
 */

import { type Cue, cueEnd, cueStart, highlightSpans } from "./cues";
import type { Geometry } from "./geometry";
import { type Style, assColour } from "./presets";
import { repr, round } from "./py";
import { lineWidth } from "./shaping";
import { type Milliseconds, type Pixels, px } from "./units";

export const FORMAT_FIELDS = [
  "Layer",
  "Start",
  "End",
  "Style",
  "Name",
  "MarginL",
  "MarginR",
  "MarginV",
  "Effect",
  "Text",
] as const;

export const LAYER_BOX = 0;
export const LAYER_TEXT = 1;
export const LAYER_ACCENT = 2;

const STYLE_NAME = "Cap";
const BOX_STYLE_NAME = "Box";

/**
 * A Dialogue row does not match the declared Format. The bug that produces no
 * error at all: a short Format makes Text swallow the remainder, and every
 * caption renders with a leading comma.
 */
export class FieldCountError extends Error {}

/** ASS timestamps are H:MM:SS.cc -- centiseconds, not milliseconds. */
export function assTime(milliseconds: number): string {
  const centiseconds = Math.floor(Math.trunc(milliseconds) / 10);
  const cs = centiseconds % 100;
  const seconds = Math.floor(centiseconds / 100) % 60;
  const minutes = Math.floor(centiseconds / 6000) % 60;
  const hours = Math.floor(centiseconds / 360000);
  const pad = (value: number): string => String(value).padStart(2, "0");
  return `${hours}:${pad(minutes)}:${pad(seconds)}.${pad(cs)}`;
}

function tupleRepr(values: readonly string[]): string {
  const items = values.map(repr);
  return items.length === 1 ? `(${items[0]},)` : `(${items.join(", ")})`;
}

/** Build one Dialogue row, refusing anything that would corrupt the format. */
export function dialogue(...fields: string[]): string {
  if (fields.length !== FORMAT_FIELDS.length) {
    throw new FieldCountError(`expected ${FORMAT_FIELDS.length} fields, got ${fields.length}: ${tupleRepr(fields)}`);
  }
  const head = fields.slice(0, -1);
  head.forEach((value, index) => {
    if (value.includes(",")) {
      throw new FieldCountError(`field ${FORMAT_FIELDS[index]} contains a comma: ${repr(value)}`);
    }
  });
  return `Dialogue: ${fields.join(",")}`;
}

/** Python's str.splitlines(): every line boundary Python recognises. */
const LINE_BREAKS = new RegExp(
  "\\r\\n|[" +
    String.fromCharCode(0x0a, 0x0d, 0x0b, 0x0c, 0x1c, 0x1d, 0x1e, 0x85, 0x2028, 0x2029) +
    "]",
  "u",
);

function splitLines(document: string): string[] {
  const lines = document.split(LINE_BREAKS);
  return lines.length && lines[lines.length - 1] === "" ? lines.slice(0, -1) : lines;
}

/** Python's str.split(separator, maxsplit): the remainder stays in the last piece. */
function splitMax(text: string, separator: string, maxSplit: number): string[] {
  const out: string[] = [];
  let rest = text;
  while (out.length < maxSplit) {
    const at = rest.indexOf(separator);
    if (at < 0) break;
    out.push(rest.slice(0, at));
    rest = rest.slice(at + separator.length);
  }
  out.push(rest);
  return out;
}

function afterColon(line: string): string {
  return splitMax(line, ":", 1)[1] ?? "";
}

/** Assert every Dialogue row matches the Format the document declares. */
export function validate(document: string): void {
  let declared: number | null = null;
  for (const line of splitLines(document)) {
    if (line.startsWith("Format:") && line.includes("Layer")) {
      declared = afterColon(line).split(",").length;
    } else if (line.startsWith("Dialogue:")) {
      if (declared === null) throw new FieldCountError("a Dialogue row appears before any Format line");
      const fields = splitMax(afterColon(line), ",", declared - 1);
      if (fields.length !== declared) {
        throw new FieldCountError(`Format declares ${declared} fields but a row has ${fields.length}: ${line}`);
      }
      if (declared !== FORMAT_FIELDS.length) {
        throw new FieldCountError(`Format declares ${declared} fields; this writer emits ${FORMAT_FIELDS.length}`);
      }
    }
  }
}

/** &H00BBGGRR from a style table becomes &HBBGGRR& for an inline override. */
export function inlineColour(colour: string): string {
  const body = colour.startsWith("&H") ? colour.slice(2) : colour;
  return `&H${body.slice(2)}&`;
}

export const STYLE_FORMAT =
  "Format: Name, Fontname, Fontsize, PrimaryColour, SecondaryColour, OutlineColour, " +
  "BackColour, Bold, Italic, Underline, StrikeOut, ScaleX, ScaleY, Spacing, Angle, " +
  "BorderStyle, Outline, Shadow, Alignment, MarginL, MarginR, MarginV, Encoding";

export function scriptInfo(width: number, height: number): string {
  return (
    "[Script Info]\nScriptType: v4.00+\n" +
    `PlayResX: ${width}\nPlayResY: ${height}\n` +
    "WrapStyle: 2\nScaledBorderAndShadow: yes\n"
  );
}

/** The Cap style's outline: black, opaque. */
export const OUTLINE_COLOUR = "&H00000000";

function header(layout: CaptionLayout): string {
  const { geometry, style, fontsize, outline } = layout;
  const boxColour = style.box ?? style.text;
  const family = geometry.face.family;
  return (
    scriptInfo(geometry.width, geometry.height) +
    "\n[V4+ Styles]\n" +
    `${STYLE_FORMAT}\n` +
    `Style: ${STYLE_NAME},${family},${fontsize},${style.text},${style.text},` +
    `${layout.outlineColour},&H00000000,-1,0,0,0,100,100,0,0,1,${outline},0,1,0,0,0,204\n` +
    `Style: ${BOX_STYLE_NAME},${family},${fontsize},${boxColour},` +
    `${boxColour},&H00000000,&H00000000,-1,0,0,0,100,100,0,0,1,0,0,7,0,0,0,204\n` +
    "\n[Events]\n" +
    `Format: ${FORMAT_FIELDS.join(", ")}\n`
  );
}

/**
 * Bottom-anchored: the block grows upward, so the edge nearest platform UI
 * never moves, whatever the line count.
 */
export function lineBaseline(geometry: Geometry, index: number, count: number): number {
  const bottom = geometry.height - geometry.bottomMargin;
  return bottom - (count - 1 - index) * geometry.lineAdvance;
}

/** One caption line's box, in source pixels: exactly one line advance tall. */
export function boxRect(
  geometry: Geometry,
  line: readonly string[],
  baseline: number,
): { left: number; top: number; right: number; bottom: number } {
  const width = lineWidth(line, geometry.textWidth, geometry.wordGap);
  return {
    left: round((geometry.width - width) / 2 - geometry.padX),
    right: round((geometry.width + width) / 2 + geometry.padX),
    top: round(baseline - geometry.lineAdvance),
    bottom: round(baseline),
  };
}

/** Where each word of a line starts, before rounding: left to right from the centred line's edge. */
export function wordStarts(geometry: Geometry, line: readonly string[]): Pixels[] {
  const width = lineWidth(line, geometry.textWidth, geometry.wordGap);
  let x = px((geometry.width - width) / 2);
  return line.map((word) => {
    const start = x;
    x = px(x + geometry.textWidth(word) + geometry.wordGap);
    return start;
  });
}

export interface Rect {
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
}

/** One word where libass draws it: `\an1\pos(x,y)`, the bottom-left of its line box. */
export interface PlacedWord {
  /** The word's place in the transcript: what makes it the same word in two layouts. */
  readonly index: number;
  readonly text: string;
  readonly x: number;
  readonly y: number;
  /** When the accent layer shows it, or never. */
  readonly accent: readonly [Milliseconds, Milliseconds] | null;
}

export interface PlacedLine {
  readonly box: Rect | null;
  readonly words: readonly PlacedWord[];
}

export interface PlacedCue {
  readonly start: Milliseconds;
  readonly end: Milliseconds;
  readonly lines: readonly PlacedLine[];
}

/** Every caption of a clip, placed: what the ASS document says and what the page paints. */
export interface CaptionLayout {
  readonly outlineColour: string;
  readonly geometry: Geometry;
  readonly style: Style;
  /** The Fontsize libass is given: whole pixels. */
  readonly fontsize: number;
  /** The outline libass is given, whole pixels; 0 for the boxed styles. */
  readonly outline: number;
  readonly cues: readonly PlacedCue[];
}

/**
 * Place every word and box of every cue in source pixels. `accent` overrides
 * the style's highlight colour with a hex value, for one clip.
 */
export function placeCaptions(
  cues: readonly Cue[],
  geometry: Geometry,
  base: Style,
  videoDurationMs: number,
  accent: string | null = null,
  outline: string | null = null,
): CaptionLayout {
  const style = accent === null ? base : { ...base, accent: assColour(accent) };
  let index = 0;
  const placed = cues.map((cue, cueIndex): PlacedCue => {
    const end = cueEnd(cues, cueIndex, videoDurationMs);
    // Keyed by text, as the document always was: a word said twice in one cue
    // lights up on its last occurrence both times.
    const spans = new Map<string, readonly [Milliseconds, Milliseconds]>();
    for (const [text, a, b] of highlightSpans(cue, end)) spans.set(text, [a, b]);
    const lines = cue.lines.map((line, lineIndex): PlacedLine => {
      const baseline = lineBaseline(geometry, lineIndex, cue.lines.length);
      const starts = wordStarts(geometry, line);
      const y = round(baseline - geometry.padY);
      return {
        box: style.box === null ? null : boxRect(geometry, line, baseline),
        words: line.map((text, position) => ({
          index: index++,
          text,
          x: round(starts[position] ?? 0),
          y,
          accent: spans.get(text) ?? null,
        })),
      };
    });
    return { start: cueStart(cue), end, lines };
  });
  return {
    geometry,
    style,
    outlineColour: outline === null ? OUTLINE_COLOUR : assColour(outline),
    fontsize: round(geometry.fontsize),
    outline: style.stroked ? round(geometry.stroke) : 0,
    cues: placed,
  };
}

function boxEvent(style: Style, rect: Rect, start: string, end: string): string {
  const { left, top, right, bottom } = rect;
  const drawing = `m ${left} ${top} l ${right} ${top} l ${right} ${bottom} l ${left} ${bottom}`;
  const colour = inlineColour(style.box ?? style.text);
  return dialogue(
    String(LAYER_BOX),
    start,
    end,
    BOX_STYLE_NAME,
    "",
    "0",
    "0",
    "0",
    "",
    `{\\an7\\pos(0,0)\\1c${colour}\\bord0\\shad0\\p1}${drawing}{\\p0}`,
  );
}

function wordEvent(layer: number, colour: string, word: PlacedWord, start: string, end: string): string {
  const tags = `{\\an1\\pos(${word.x},${word.y})\\1c${inlineColour(colour)}\\shad0}`;
  return dialogue(String(layer), start, end, STYLE_NAME, "", "0", "0", "0", "", `${tags}${word.text}`);
}

/** The ASS document for a placed layout. */
export function layoutAss(layout: CaptionLayout): string {
  const { style } = layout;
  const events: string[] = [];
  for (const cue of layout.cues) {
    const start = assTime(cue.start);
    const stop = assTime(cue.end);
    for (const line of cue.lines) {
      if (line.box !== null) events.push(boxEvent(style, line.box, start, stop));
      for (const word of line.words) {
        events.push(wordEvent(LAYER_TEXT, style.text, word, start, stop));
        if (word.accent !== null) {
          events.push(wordEvent(LAYER_ACCENT, style.accent, word, assTime(word.accent[0]), assTime(word.accent[1])));
        }
      }
    }
  }
  const document = header(layout) + events.join("\n") + (events.length ? "\n" : "");
  validate(document);
  return document;
}

/**
 * Render cues into a complete ASS document. `accent` overrides the style's
 * highlight colour with a hex value, for one clip.
 */
export function writeAss(
  cues: readonly Cue[],
  geometry: Geometry,
  base: Style,
  videoDurationMs: number,
  accent: string | null = null,
): string {
  return layoutAss(placeCaptions(cues, geometry, base, videoDurationMs, accent));
}
